import { useEffect } from 'react';

/**
 * Background scroll lock for anything that covers the page (modals, sheets, drawers).
 * Reference-counted: overlays that open on top of each other release the page only when
 * the last one closes. The lock sits on <html> as well as <body> (iOS Safari ignores
 * `body { overflow: hidden }` alone) and keeps a classic scrollbar's gutter so nothing
 * jumps. Port of the platform's `useModalLock`.
 */
let lockCount = 0;
let saved = null;

function acquire() {
  lockCount += 1;
  if (lockCount > 1) return;
  const html = document.documentElement;
  const body = document.body;
  const scrollbar = window.innerWidth - html.clientWidth;
  saved = {
    htmlOverflow: html.style.overflow,
    htmlOverscroll: html.style.overscrollBehavior,
    htmlGutter: html.style.scrollbarGutter,
    bodyOverflow: body.style.overflow,
    bodyPaddingRight: body.style.paddingRight,
  };
  html.style.overflow = 'hidden';
  html.style.overscrollBehavior = 'none';
  body.style.overflow = 'hidden';
  if (scrollbar > 0) {
    const gutter = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('scrollbar-gutter', 'stable');
    if (gutter) html.style.scrollbarGutter = 'stable';
    else body.style.paddingRight = `${scrollbar}px`;
  }
  body.classList.add('modal-open');
}

function release() {
  if (lockCount === 0) return;
  lockCount -= 1;
  if (lockCount > 0) return;
  const html = document.documentElement;
  const body = document.body;
  if (saved) {
    html.style.overflow = saved.htmlOverflow;
    html.style.overscrollBehavior = saved.htmlOverscroll;
    html.style.scrollbarGutter = saved.htmlGutter;
    body.style.overflow = saved.bodyOverflow;
    body.style.paddingRight = saved.bodyPaddingRight;
    saved = null;
  }
  body.classList.remove('modal-open');
}

export function useScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    acquire();
    return release;
  }, [active]);
}

export default useScrollLock;
